import { useContext, useState } from 'react';

import {
  Text,
  SimpleGrid,
  Box,
  Button,
  Flex,
  Collapse,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';

import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';

import { useAppDispatch } from 'hooks';

import FormInput from 'components/fields/home/FormInput';
import SelectInput from 'components/fields/home/SelectInput';

import {
  changeDwellingCoverages,
  changeDwellingCoveragesItem,
  addDwellingCoveragesItem,
  removeDwellingCoveragesItem,
} from 'reducers/homePersonalFormReducer';

import { SECTIONS, RATING_PLANS } from 'views/home_form/constants';

import isSuperAdmin from 'helpers/isSuperAdmin/isSuperAdmin';

import { AuthContext } from 'contexts/AuthContext';

import type { APIPersonalHomeApplicationForm } from 'types/api.types';

interface CoveragesProps {
  dwelling: APIPersonalHomeApplicationForm['dwellings'][0];
  index: number;
}

const Coverages = ({ dwelling, index }: CoveragesProps) => {
  const [isCollapsed, setIsCollapsed] = useState(true); // Closed by default
  const { user } = useContext(AuthContext);
  const dispatch = useAppDispatch();
  const superAdmin = isSuperAdmin(user);

  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const bgColor = useColorModeValue('', 'gray.800');
  const shadowColor = useColorModeValue(
    '0 2px 4px rgba(0,0,0,0.1)',
    '0 2px 4px rgba(0,0,0,0.3)',
  );

  const onCoverageChange = (event: any) => {
    const { name, value } = event.target;

    dispatch(
      changeDwellingCoverages({
        index,
        name,
        value,
      }),
    );
  };

  const onCoverageItemChange = (event: any, itemIndex: number) => {
    const { name, value } = event.target;

    dispatch(
      changeDwellingCoveragesItem({
        dwellingIndex: index,
        itemIndex,
        name,
        value,
      }),
    );
  };

  const handleAddCoverageItem = () => {
    if (!dwelling.coverages?.items || dwelling.coverages.items.length < 20) {
      dispatch(addDwellingCoveragesItem(index));
    }
  };

  const handleRemoveCoverageItem = (itemIndex: number) => {
    dispatch(removeDwellingCoveragesItem({ dwellingIndex: index, itemIndex }));
  };

  return (
    <Box
      border="1px solid"
      borderColor={borderColor}
      borderRadius="md"
      bg={bgColor}
      boxShadow={shadowColor}
      overflow="hidden"
      mb="20px"
    >
      <Box
        rounded="5px"
        p="10px"
        backgroundColor="brand.400"
        color="white"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box fontWeight="medium">Coverages</Box>
        <Button
          variant="ghost"
          size="sm"
          color="white"
          _hover={{ bg: 'whiteAlpha.200' }}
          onClick={() => setIsCollapsed(!isCollapsed)}
          rightIcon={
            <Icon as={isCollapsed ? ChevronDownIcon : ChevronUpIcon} />
          }
        >
          {isCollapsed ? 'Expand' : 'Collapse'}
        </Button>
      </Box>

      <Collapse in={!isCollapsed} animateOpacity>
        <Box p="6">
          <SimpleGrid columns={6} spacing="20px" mb="20px">
            <FormInput
              section={SECTIONS.DWELLINGS}
              label="Coverage form type"
              name="coverage_form_type"
              value={dwelling.coverages.coverage_form_type}
              onChange={onCoverageChange}
              disabled
            />

            <SelectInput
              section={SECTIONS.DWELLINGS}
              label="Rating plan"
              name="rating_plan"
              value={dwelling.coverages.rating_plan}
              options={[
                { value: '', label: 'Select...' },
                ...RATING_PLANS.map((plan) => ({
                  value: plan,
                  label: plan,
                })),
              ]}
              onChange={onCoverageChange}
            />
          </SimpleGrid>

          {dwelling.coverages.items && dwelling.coverages.items.length > 0 ? (
            <>
              <Text fontWeight="medium" fontSize="md" mb="15px">
                Coverage Items
              </Text>

              {dwelling.coverages.items.map((item: any, itemIndex: number) => (
                <Box
                  key={itemIndex}
                  p="15px"
                  border="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                  mt={itemIndex === 0 ? '0' : '15px'}
                >
                  <Flex justify="space-between" align="center" mb="15px">
                    <Text fontWeight="medium" fontSize="sm">
                      Coverage Item {itemIndex + 1}
                    </Text>
                    {superAdmin && (
                      <Button
                        size="xs"
                        variant="outline"
                        onClick={() => handleRemoveCoverageItem(itemIndex)}
                      >
                        Remove
                        <Text textColor="red" display="inline" ml="5px">
                          (dev mode)
                        </Text>
                      </Button>
                    )}
                  </Flex>

                  <SimpleGrid columns={6} spacing="20px">
                    <FormInput
                      section={SECTIONS.DWELLINGS}
                      label="Coverage description"
                      name="coverage_description"
                      value={item.coverage_description}
                      onChange={(event: any) =>
                        onCoverageItemChange(event, itemIndex)
                      }
                      disabled={!item.can_remove}
                    />

                    <SelectInput
                      section={SECTIONS.DWELLINGS}
                      label="Requested/declined"
                      name="requested_declined"
                      options={[
                        { value: '', label: 'Select...' },
                        { value: 'requested', label: 'Requested' },
                        { value: 'declined', label: 'Declined' },
                      ]}
                      value={item.requested_declined}
                      onChange={(event: any) =>
                        onCoverageItemChange(event, itemIndex)
                      }
                      disabled
                    />

                    <FormInput
                      section={SECTIONS.DWELLINGS}
                      label="Amount of insurance"
                      name="amount_of_insurance"
                      value={item.amount_of_insurance}
                      onChange={(event: any) =>
                        onCoverageItemChange(event, itemIndex)
                      }
                      disabled
                    />

                    <FormInput
                      section={SECTIONS.DWELLINGS}
                      label="Deductible"
                      name="deductible"
                      value={item.deductible}
                      onChange={(event: any) =>
                        onCoverageItemChange(event, itemIndex)
                      }
                      disabled
                    />

                    <FormInput
                      section={SECTIONS.DWELLINGS}
                      label="Deductible type"
                      name="deductible_type"
                      value={item.deductible_type}
                      onChange={(event: any) =>
                        onCoverageItemChange(event, itemIndex)
                      }
                      disabled
                    />

                    <FormInput
                      section={SECTIONS.DWELLINGS}
                      label="Estimated premium"
                      name="estimated_premium"
                      value={item.estimated_premium}
                      onChange={(event: any) =>
                        onCoverageItemChange(event, itemIndex)
                      }
                      disabled
                    />
                  </SimpleGrid>
                </Box>
              ))}
            </>
          ) : (
            <Text fontSize="md" color="gray.600" fontStyle="italic">
              No coverage items added
            </Text>
          )}

          {superAdmin && (
            <Box mt="15px">
              <Button
                size="sm"
                colorScheme="blue"
                variant="outline"
                onClick={handleAddCoverageItem}
              >
                Add Coverage Item ({dwelling.coverages.items?.length || 0}/20)
                <Text textColor="red" display="inline" ml="10px">
                  (dev mode)
                </Text>
              </Button>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default Coverages;
